import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { CustomFormControlAccessWithLoaders } from 'src/app/_core/tools/custom-form-control-access';
import { CleRepartitionModelDto } from 'src/graphql/generated-types';
import { AbstractControl, FormArray, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { GraphqlService } from 'src/app/_core/services/graphql.service';
import {
    AnalyticalPlanAndSections,
    AnalyticalPlanState
} from 'src/app/_shared/stores/analytical-plan/analytical-plan.state';
import { BehaviorSubject, combineLatest, merge, Observable, of, Subscription, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import {
    AnalyticalPlanInitAnalyticalParametersAction
} from 'src/app/_shared/stores/analytical-plan/analytical-plan.actions';
import { buildProviders, Coerce } from '@kalidea/kaligraphi';
import { arrayEmpty, arrayNotEmpty } from 'src/app/utils/array.utils';
import { FormValidator } from 'src/app/validators/form-validator';
import { isEmpty } from 'src/app/utils/object.utils';
import cloneDeep from 'lodash-es/cloneDeep';
import { toNumber } from 'src/app/utils/number.utils';
import { flatten } from 'lodash-es';

type PlanDistribution = { IdPlanAnalytique: number, IdSectionAnalytique: number, Pourcentage: number, Montant?: number };

@Component({
    selector: 'app-shared-analytical-plan-distributer',
    templateUrl: './shared-analytical-plan-distributer.component.html',
    styleUrls: ['./shared-analytical-plan-distributer.component.sass'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: buildProviders(SharedAnalyticalPlanDistributerComponent),
    standalone: false
})
export class SharedAnalyticalPlanDistributerComponent extends CustomFormControlAccessWithLoaders implements OnInit {

    analyticalPlansAndSectionsList: AnalyticalPlanAndSections[] = [];

    form: FormGroup;

    analyticalPlanDistributerErrorsMessages: Record<string, string> = {
        noSectionDefinedForPlan: 'Une section n\'est pas renseignée.',
        repartitionIsNotHundredPercent: 'Le plan n\'a pas une répartition de 100%.',
        notuniquesectionforplan: 'Il y a une duplication de section.',
        repartitionWrongLineAmount: 'Le montant de répartition est obligatoire sur chaque ligne et doit être supérieur à zéro.'
    };

    displayedErrorsMessages: string[][] = [];

    @Input()
    @Coerce('boolean')
    shouldHideAmountColumn = false;

    @Coerce('boolean')
    @Input()
    required: boolean;

    private sectionSubscriptionsList: Subscription[] = [];

    private distributionKeysListSubscription: Subscription;
    private _recordInformations = new BehaviorSubject<CleRepartitionModelDto[]>(null);

    constructor(protected readonly cdr: ChangeDetectorRef,
                private readonly graphqlService: GraphqlService,
                private readonly fb: FormBuilder) {
        super(cdr);
    }

    private _distributionKeysList = new BehaviorSubject<CleRepartitionModelDto[]>([]);

    get distributionKeysList(): CleRepartitionModelDto[] {
        return this._distributionKeysList.getValue();
    }

    get distributionKeysList$(): Observable<CleRepartitionModelDto[]> {
        return this._distributionKeysList.asObservable();
    }

    get planAndSections(): FormArray {
        return this.form?.get('planAndSections') as FormArray;
    }

    private _amount = 0;

    get amount(): number {
        return this._amount;
    }

    @Input() set amount(amount: number) {
        this._amount = Math.abs(toNumber('' + amount));

        if (arrayNotEmpty(this.planAndSections?.controls)) {
            this.planAndSections.controls.forEach(e => {
                (e.get('ListeSections') as FormArray).controls.forEach(section => {
                    if (!this.shouldHideAmountColumn) {
                        const sectionAmountControl = section.get('Montant');
                        sectionAmountControl.patchValue(section.get('Pourcentage').value * +this.amount / 100, {emitEvent: false});
                        sectionAmountControl.setValidators([FormValidator.isNotNull, Validators.max(+this.amount)]);
                        sectionAmountControl.updateValueAndValidity({emitEvent: false});
                    }
                });
            });

            this.notifyUpdate(this.normalize(this.planAndSections.value));
        }
    }

    private get recordInformations$(): Observable<CleRepartitionModelDto[]> {
        return this._recordInformations.asObservable();
    }

    validate(c: AbstractControl): Observable<{}> {
        return of(this.planAndSections.valid ? null : {analyticalRepartition: true});
    }

    deleteSection(sectionsList: AbstractControl, index: number): void {
        (sectionsList as FormArray).removeAt(index);
    }

    private sectionListFromPlan(plan: AbstractControl): FormArray {
        return plan.get('ListeSections') as FormArray;
    }

    addSection(plan: AbstractControl): void {
        this.sectionListFromPlan(plan).push(this.createSectionForm(plan));
    }

    isSectionListEmpty(plan: AbstractControl): boolean {
        return arrayEmpty(this.sectionListFromPlan(plan).controls);
    }

    writeValue(value: CleRepartitionModelDto[]): void {
        this._distributionKeysList.next(value || []);
        this._recordInformations.next(value);
        super.writeValue(value);
    }

    getErrorMessages(errorObject: ValidationErrors): string[] {
        return Object.keys(errorObject).map(e => this.analyticalPlanDistributerErrorsMessages[e]);
    }

    private updateForm(): void {
        this.sectionSubscriptionsList.forEach(e => e.unsubscribe());
        this.sectionSubscriptionsList = [];

        this.planAndSections.clear({emitEvent: false});
        this.analyticalPlansAndSectionsList.forEach(planAndSections => {
            this.planAndSections.push(this.createPlanForm(planAndSections), {emitEvent: false});
        });
        this.planAndSections.updateValueAndValidity();

        this.cdr.markForCheck();
    }

    private initSubscription(): void {
        this.watch$ = combineLatest([
            this.recordInformations$,
            this.distributionKeysList$,
        ]).pipe(
            tap(() => this.updateForm())
        );

        this.distributionKeysListSubscription?.unsubscribe();
        this.distributionKeysListSubscription = this.planAndSections.valueChanges.pipe(
            this.untilDestroyed,
            tap(formValues => {
                this.notifyUpdate(this.normalize(formValues));
                this.cdr.markForCheck();
            })
        ).subscribe();
    }

    private createPlanForm(planAndSections?: AnalyticalPlanAndSections): FormGroup {
        const plan = this.fb.group({
            Id: [planAndSections?.plan?.Id || null],
            Libelle: [planAndSections?.plan?.Libelle || null],
        }, {
            validators: [
                form => {
                    const errors: any = {};

                    // Pas de section sélectionnée
                    if (form.value.ListeSections?.some(s => s.IdSectionAnalytique === null)) {
                        errors.noSectionDefinedForPlan = true;
                    }

                    // même sections sélectionnées
                    if (arrayNotEmpty(form.value.ListeSections)
                        && new Set(form.value.ListeSections.map(s => s.IdSectionAnalytique)).size !== form.value.ListeSections.length) {
                        errors.notuniquesectionforplan = true;
                    }

                    // Total des pourcentage != 100%
                    const sectionPercentage = form.value.ListeSections?.reduce((acc, currentValue) => acc + (+currentValue.Pourcentage || 0), 0);
                    if (arrayNotEmpty(form.value.ListeSections) && sectionPercentage !== 100) {
                        errors.repartitionIsNotHundredPercent = true;
                    }

                    // Pas de ligne à 0
                    if (arrayNotEmpty(form.value.ListeSections) && form.value.ListeSections.some(s => s.Pourcentage === 0)) {
                        errors.repartitionWrongLineAmount = true;
                    }

                    return isEmpty(errors) ? null : errors;
                }
            ]
        });

        const sectionsList = this.distributionKeysList.filter(e => e.IdPlanAnalytique === planAndSections?.plan?.Id).map(e => {
            return this.createSectionForm(plan, e);
        });

        plan.addControl('ListeSections', this.fb.array(sectionsList));
        return plan;
    }

    private createSectionForm(plan: AbstractControl, distributionKey?: CleRepartitionModelDto): FormGroup {
        const sectionsAmount = this.sectionListFromPlan(plan)?.value?.reduce((acc, current) => {
            acc += +(current.Montant || 0);
            return acc;
        }, 0) || 0;

        let Pourcentage = null;
        let Montant = null;

        if (distributionKey?.IdSectionAnalytique) {
            if (distributionKey?.Pourcentage) {
                Pourcentage = +distributionKey.Pourcentage * 100;
            } else {
                Pourcentage = distributionKey?.Montant ? ((+distributionKey.Montant / this.amount) * 100) : 100;
            }

            if (!this.shouldHideAmountColumn) {
                if (distributionKey?.Montant) {
                    Montant = +distributionKey.Montant;
                } else {
                    Montant = distributionKey?.Pourcentage ? (this.amount * +distributionKey?.Pourcentage) : this.amount;
                }
            }

        } else {
            Pourcentage = this.amount ? (100 - (sectionsAmount * 100 / this.amount)) : 100;
            Montant = this.amount ? (this.amount - sectionsAmount) : 0;
        }

        Pourcentage = Math.abs(Pourcentage);
        Montant = Math.abs(Montant);

        const section = this.fb.group({
            IdSectionAnalytique: [distributionKey?.IdSectionAnalytique || null, [FormValidator.isNotNull]],
            Pourcentage: [Pourcentage, [FormValidator.isNotNull, Validators.min(0), Validators.max(100)]],
            Montant: [Montant, [FormValidator.isNotNull, Validators.min(0), Validators.max(+this.amount)]]
        });

        if (this.shouldHideAmountColumn) {
            section.get('Montant').clearValidators();
        }

        this.sectionSubscriptionsList.push(
            merge(
                section.get('Pourcentage').valueChanges.pipe(
                    tap(value => {
                        section.get('Montant').patchValue(this.amount * value / 100, {emitEvent: false});
                    })
                ),
                section.get('Montant').valueChanges.pipe(
                    tap(value => {
                        section.get('Pourcentage').patchValue(this.amount ? (value * 100 / this.amount) : 100, {emitEvent: false});
                    })
                )
            ).pipe(
                this.untilDestroyed
            ).subscribe()
        );

        return section;
    }

    private normalize(newValues): PlanDistribution[] {
        return flatten(cloneDeep(newValues).map(e => {
            return e.ListeSections.map(section => {
                let initialObj: PlanDistribution = {
                    IdPlanAnalytique: e.Id,
                    IdSectionAnalytique: section.IdSectionAnalytique,
                    Pourcentage: section.Pourcentage / 100
                };

                if (!this.shouldHideAmountColumn) {
                    initialObj.Montant = section.Montant;
                }
                return initialObj;
            });
        }));
    }

    ngOnInit(): void {
        this.form = this.fb.group({
            planAndSections: this.fb.array([])
        });

        this.loading = true;
        this.watch$ = this.store.dispatch(new AnalyticalPlanInitAnalyticalParametersAction()).pipe(
            tap(() => {
                this.analyticalPlansAndSectionsList = this.snapshot(AnalyticalPlanState.analyticalPlansAndSections) || [];
                this.initSubscription();
                this.loading = false;
            }),
            catchError(err => {
                this.loading = false;
                return throwError(() => err);
            })
        );
    }
}
